import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  getLocalStorageRoleNames,
  isTeamManager,
  SelfServeLocalStorageUtil,
} from '../utils/local-storage.util';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { PersonalizedPracticeService } from '../services/personalized-practice/personalized-practice.service';
import { AppConstants } from '../app-constants';

export const roleGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const featureFlagsService = inject(FeatureFlagsService);
  const personalizedPracticeService = inject(PersonalizedPracticeService);
  const url: string = state.url;
  const userRoles: string[] = getLocalStorageRoleNames();
  const hasSimulationBuilderAccess: boolean = SelfServeLocalStorageUtil.hasSimulationBuilderAccess();

  const homeUrls: string[] = ['', '/', '/home', '/team_manager', '/team_manager/home'];
  const teamAdminUrls: string[] = ['/team_manager', '/team_manager/home', '/team_manager/my_teams'];
  const enablementAdminUrls: string[] = ['/reporting', '/reporting_visualization'];
  const simulationBuilderUrl: string[] = ['/self-serve'];
  const reportingUrl: string[] = ['/reporting_dashboard'];
  const leaderboardUrl: string[] = ['/leaderboard'];
  const personalizedPracticeUrl: string[] = ['/personalized-practice'];
  const learningPathwayUrl: string[] = ['/learning-pathway'];
  const isTeamAdmin = isTeamManager();
  const isLearner: boolean = userRoles.includes("Learner");
  const enablementAdmin: boolean = userRoles.includes("EnablementAdmin");
  const isLeaderboardEnabled = await featureFlagsService.isFlagEnabled(AppConstants.LEADERBOARD_UI);
  const isMyTeamsEnabled = await featureFlagsService.isFlagEnabled(AppConstants.MY_TEAMS_UI);
  const isReportingUrlEnabled = await featureFlagsService.isFlagEnabled(AppConstants.NEW_REPORTING_UI);
  const isReportingV1Enabled = await featureFlagsService.isFlagEnabled(AppConstants.REPORTING_V1);
  const isPersonalizedPracticeEnabled = await featureFlagsService.isFlagEnabled(AppConstants.PERSONALISED_PRACTICE_UI);
  const hasPersonalizedPracticeProducts = await firstValueFrom(
    personalizedPracticeService.hasPersonalizedPracticeProducts$
  );
  const isSimulationReportViewEnabled = await featureFlagsService.isFlagEnabled(AppConstants.SIMULATION_REPORT_VIEW);
  const isLearningPathwaysEnabled = await featureFlagsService.isFlagEnabled('Learning_Pathways');

  if ((simulationBuilderUrl.includes(url) && !hasSimulationBuilderAccess)) {
    return router.parseUrl('/');
  }

  if (isTeamAdmin && isMyTeamsEnabled && homeUrls.includes(url)) {
    return router.parseUrl('/team_manager/my_teams');
  }


  if (!isTeamAdmin && isLearner && homeUrls.includes(url)) {
    return router.parseUrl('/assignments');
  }

  if (!isTeamAdmin && !isLearner && enablementAdmin && isReportingV1Enabled && homeUrls.includes(url)) {
    return router.parseUrl('/reporting');
  }

  if (!isTeamAdmin && !isLearner && !enablementAdmin && hasSimulationBuilderAccess && homeUrls.includes(url)) {
    return router.parseUrl('/self-serve');
  }
  
  // Redirect to personalized practice only if learner, feature flag enabled, and products available
  if (!isTeamAdmin && isLearner && isPersonalizedPracticeEnabled && hasPersonalizedPracticeProducts && homeUrls.includes(url)) {
    return router.parseUrl('/personalized-practice');
  }

  if (isTeamAdmin && !isMyTeamsEnabled && isLearner && homeUrls.includes(url)) {
    return router.parseUrl('/assignments');
  }

  if (isLeaderboardEnabled && (isLearner || enablementAdmin || isTeamAdmin) && homeUrls.includes(url)) {
    return router.parseUrl('/leaderboard');
  }

  if (!isTeamAdmin && teamAdminUrls.includes(url)) {
    return router.parseUrl('/');
  }

  if ((!enablementAdmin || !isReportingV1Enabled) && enablementAdminUrls.includes(state.url.split('?')[0])) {
    return router.parseUrl('/');
  }

  if (!isTeamAdmin && !isLearner && ['/assignments', '/team_manager/assignments', '/team_manager/my_teams'].includes(url)) {
    return router.parseUrl('/');
  }

  if ((!isTeamAdmin && !enablementAdmin || !isReportingUrlEnabled) && reportingUrl.includes(url)) {
    return router.parseUrl('/');
  }

  if (!isLeaderboardEnabled && leaderboardUrl.includes(url)) {
    return router.parseUrl('/');
  }

  // Block direct access to learning-pathway when Learning_Pathways flag is off
  if (!isLearningPathwaysEnabled && learningPathwayUrl.includes(state.url.split('?')[0])) {
    return router.parseUrl('/assignments');
  }

  if ((!isLearner || !isPersonalizedPracticeEnabled || !hasPersonalizedPracticeProducts) && personalizedPracticeUrl.includes(url)) {
    return router.parseUrl('/');
  }

  if (!isMyTeamsEnabled && url.includes('/team_manager/my_teams')) {
    return router.parseUrl('/');
  }

  if (!isSimulationReportViewEnabled && url.includes('/reporting_dashboard/simulation/')) {
    return router.parseUrl('/reporting_dashboard');
  }

  return true;

};
